"use client";

import { c as _c } from "react/compiler-runtime";
import { readFragment } from "@opensea/graphql";
import { BottomSheetBody, BottomSheetFooter, BottomSheetHeader, BottomSheetHeaderTitle, Flex, Form, Media, Separator, SkeletonBlock, tv } from "@opensea/ui-kit";
import { useLocalStorage } from "@opensea/ui-kit/hooks";
import { useMemo } from "react";
import { Controller, useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";
import { Button } from "../../../../../components/Button";
import { CreateOrdersSummary, CreateOrdersSummarySkeleton } from "../../../../../components/CreateOrdersSummary";
import { ExpirationSelect } from "../../../../../components/ExpirationSelect";
import { useIsFormSubmitDisabled } from "../../../../../lib/form/valid";
import { DEFAULT_EXPIRATION, OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE, OFFER_EXPIRATION_LOCAL_STORAGE } from "../../../constants";
import { CreateOfferFlowHeaderActions, CreateOfferFlowHeaderActionsSkeleton } from "../CreateOfferFlowHeaderActions";
import { CreateOfferFlowTable, CreateOfferFlowTableSkeleton } from "../CreateOfferFlowTable/CreateOfferFlowTable";
import { CreateOfferForm_itemFragment } from "./CreateOfferForm.fragment";
const footerVariants = tv({
  slots: {
    buttonContainer: "grid grid-cols-2 gap-3 pb-4 md:flex md:justify-end md:pb-0"
  }
});
export const CreateOfferForm = ({
  items: itemKeys,
  onSubmit
}) => {
  const t = useTranslations("wallet.CreateOfferForm");
  const items = itemKeys.map(itemKey => readFragment(CreateOfferForm_itemFragment, itemKey));
  const {
    control,
    handleSubmit
  } = useFormContext();
  const {
    errors
  } = useFormState({
    control
  });
  const offers = useWatch({
    control,
    name: "offers"
  });
  const firstOffer = offers[0];
  const tableItems = offers.flatMap(offer => {
    const matchedItem = items.find(item => item.id === offer.id);
    return matchedItem ? [matchedItem] : [];
  });
  const [_, setOfferExpiration] = useLocalStorage(OFFER_EXPIRATION_LOCAL_STORAGE, DEFAULT_EXPIRATION);
  const [__, setOfferExpirationCustom] = useLocalStorage(OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE, undefined);
  const isFormSubmitDisabled = useIsFormSubmitDisabled(control);
  const footer = useMemo(() => {
    return <Flex className={footerVariants().buttonContainer()}>
        <Controller control={control} name="expiration" render={({
        field
      }) => <ExpirationSelect className="w-full" customDate={field.value.customExpiration} error={errors.expiration?.customExpiration?.message} onChange={value => {
        field.onChange({
          ...field.value,
          expirationType: value
        });
        setOfferExpiration(value);
      }} onCustomDateChange={date => {
        field.onChange({
          ...field.value,
          customExpiration: date
        });
        setOfferExpirationCustom(date.toISOString());
      }} value={field.value.expirationType} />} />

        <Button disabled={isFormSubmitDisabled} type="submit">
          {t("reviewItemOffer", {
          count: items.length
        })}
        </Button>
      </Flex>;
  }, [control, errors, setOfferExpiration, setOfferExpirationCustom, t, isFormSubmitDisabled]);
  return <Form onSubmit={handleSubmit(onSubmit)}>
      <BottomSheetHeader className="justify-between">
        <BottomSheetHeaderTitle className="whitespace-nowrap">
          {t("createItemOffer", {
          count: items.length
        })}
        </BottomSheetHeaderTitle>
        <Media greaterThanOrEqual="md">
          <CreateOfferFlowHeaderActions offerSymbol={firstOffer?.offerSymbol || ""} />
        </Media>
      </BottomSheetHeader>

      <BottomSheetBody className="mt-2 pb-0">
        <Media lessThan="md">
          <CreateOfferFlowHeaderActions className="pb-4" offerSymbol={firstOffer?.offerSymbol || ""} />
        </Media>

        <CreateOfferFlowTable isReview={false} items={tableItems} />

        <Separator className="mt-1" />

        {items[0]?.collection ? <CreateOrdersSummary adjustForFees={false} className="md:w-fit" collection={items[0].collection} creatorFeeEnforced={firstOffer?.isCreatorFeeRequired} currencyIdentifier={{
        contractAddress: items[0].collection.chain.offerCurrency.address,
        chainIdentifier: items[0].collection.chain.identifier
      }} orderSide="offer" orders={offers} symbol={items[0].collection.chain.offerCurrency.symbol} /> : null}

        <Media lessThan="md">{footer}</Media>
      </BottomSheetBody>

      <Media greaterThanOrEqual="md">
        <BottomSheetFooter>{footer}</BottomSheetFooter>
      </Media>
    </Form>;
};
export const CreateOfferFormSkeleton = t0 => {
  const $ = _c(22);
  const {
    count
  } = t0;
  const t = useTranslations("wallet.CreateOfferForm");
  let t1;
  if ($[0] !== count || $[1] !== t) {
    t1 = t("createItemOffer", {
      count
    });
    $[0] = count;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <BottomSheetHeaderTitle>{t1}</BottomSheetHeaderTitle>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== count) {
    t3 = <Media greaterThanOrEqual="md"><CreateOfferFlowHeaderActionsSkeleton count={count} /></Media>;
    $[5] = count;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <BottomSheetHeader className="justify-between">{t2}{t3}</BottomSheetHeader>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  let t6;
  if ($[10] !== count) {
    t5 = <Media lessThan="md"><CreateOfferFlowHeaderActionsSkeleton className="pb-4" count={count} /></Media>;
    t6 = <CreateOfferFlowTableSkeleton count={count} />;
    $[10] = count;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  let t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Separator className="mt-1" />;
    t8 = <CreateOrdersSummarySkeleton orderSide="offer" />;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t5 || $[16] !== t6) {
    t9 = <BottomSheetBody>{t5}{t6}{t7}{t8}</BottomSheetBody>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <BottomSheetFooter><Flex className={footerVariants().buttonContainer()}><SkeletonBlock className="h-8 w-full rounded md:w-44" /><SkeletonBlock className="h-8 w-full rounded md:w-24" /></Flex></BottomSheetFooter>;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t4 || $[20] !== t9) {
    t11 = <>{t4}{t9}{t10}</>;
    $[19] = t4;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
};